import { useState } from "react";
import { useNavigate } from "react-router-dom";
import LayoutWide from "../layouts/LayoutWide.jsx";
import DeviceSelect from "../components/DeviceSelect.jsx";
import UnitConverter from "../components/UnitConverter.jsx";
import { useCreateInventoryItem, useInventoryQuery, useStrainCatalogQuery, useReferralLinksQuery } from "../hooks/useInventoryQuery.js";
import { UNIT_GROUPS, toBaseValue, storageHint } from "../utility/unitConversions.js";
import { BIO_CATEGORIES, BOM_CATEGORIES, PARENT_CATEGORIES, CATEGORY_LABELS, CATEGORY_ORDER } from "../utility/inventoryConstants.js";
import usePlan from "../hooks/usePlan.js";

const PLAN_LIMITS = { hobby: 5, pro: 50, enterprise: Infinity };

function UpgradeModal({ current, limit, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl max-w-sm w-full mx-4 p-6" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-lg font-semibold text-gray-900 mb-2">Inventory limit reached</h2>
        <p className="text-sm text-gray-600 mb-4">
          You&apos;ve reached your Hobby plan limit ({current}/{limit} items).<br />
          To upgrade to Pro, email us at{" "}
          <a href="mailto:hello@fungistics.com" className="text-cyan-600 underline">hello@fungistics.com</a>.
        </p>
        <button onClick={onClose} className="w-full rounded-lg bg-cyan-600 px-4 py-2 text-sm font-medium text-white hover:bg-cyan-700">Got it</button>
      </div>
    </div>
  );
}

const CATEGORIES = CATEGORY_ORDER.map((v) => ({ value: v, label: CATEGORY_LABELS[v] }));

const CONTAINER_TYPES = [
  { value: "syringe", label: "Syringe" },
  { value: "mason_jar", label: "Mason Jar" },
  { value: "agar_plate", label: "Agar Plate" },
  { value: "grain_jar", label: "Grain Jar" },
  { value: "bag", label: "Bag" },
  { value: "other", label: "Other" },
];

const STATUSES = [
  { value: "growing", label: "Growing" },
  { value: "ready", label: "Ready" },
  { value: "depleted", label: "Depleted" },
  { value: "contaminated", label: "Contaminated" },
];

function defaultStatus(cat) {
  switch (cat) {
    case "materials":
    case "supplies":
      return "ready";
    case "products":
      return "depleted";
    default:
      return "growing";
  }
}

export default function InventoryNew() {
  const navigate = useNavigate();
  const createItem = useCreateInventoryItem();
  const { data: catalog = [] } = useStrainCatalogQuery();
  const { plan, planLoading } = usePlan();
  const { data: inventoryItems = [] } = useInventoryQuery();
  const { data: referralLinks = [] } = useReferralLinksQuery();
  const [showUpgrade, setShowUpgrade] = useState(false);

  const itemCount = inventoryItems?.length ?? 0;
  const limit = PLAN_LIMITS[plan ?? "hobby"] ?? 5;
  const atLimit = !planLoading && itemCount >= limit;

  const [category, setCategory] = useState("cultures");
  const [name, setName] = useState("");
  const [catalogStrain, setCatalogStrain] = useState("");
  const [species, setSpecies] = useState("");
  const [generation, setGeneration] = useState("");
  const [containerType, setContainerType] = useState("");
  const [quantity, setQuantity] = useState("");
  const [displayUnit, setDisplayUnit] = useState("syringes");
  const [status, setStatus] = useState(defaultStatus("cultures"));
  const [startedAt, setStartedAt] = useState("");
  const [inoculatedAt, setInoculatedAt] = useState("");
  const [readyAt, setReadyAt] = useState("");
  const [deviceId, setDeviceId] = useState("");
  const [lowStockThreshold, setLowStockThreshold] = useState("");
  const [sourceName, setSourceName] = useState("");
  const [sourceUrl, setSourceUrl] = useState("");
  const [referralLinkId, setReferralLinkId] = useState("");
  const [defaultDeductQty, setDefaultDeductQty] = useState("");
  const [notes, setNotes] = useState("");
  const [showSource, setShowSource] = useState(false);
  const [parentItemId, setParentItemId] = useState("");
  const [costPerUnit, setCostPerUnit] = useState("");
  const [costMode, setCostMode] = useState("direct"); // "direct" | "calc"
  const [costQty, setCostQty] = useState("");
  const [costTotal, setCostTotal] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [error, setError] = useState(null);

  const isBio = BIO_CATEGORIES.has(category);
  const isBom = BOM_CATEGORIES.has(category);
  const showParent = PARENT_CATEGORIES.has(category);
  const showExpiry = !BOM_CATEGORIES.has(category);

  // Eligible parents: bio items that are not depleted or contaminated
  const parentOptions = inventoryItems.filter(
    (i) => BIO_CATEGORIES.has(i.category) && i.status !== "depleted" && i.status !== "contaminated"
  );

  function handleCategoryChange(cat) {
    setCategory(cat);
    setStatus(defaultStatus(cat));
    if (!PARENT_CATEGORIES.has(cat)) setParentItemId("");
    if (BOM_CATEGORIES.has(cat)) setExpiresAt("");
  }

  function handleCatalogSelection(commonName) {
    setCatalogStrain(commonName);
    const entry = catalog.find((e) => e.common_name === commonName);
    if (entry) setSpecies(entry.species);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);

    const hint = storageHint(displayUnit);
    const dbUnit = hint
      ? UNIT_GROUPS.flatMap((g) => g.units).find((u) => u.display === displayUnit)?.db
      : displayUnit;

    const body = {
      category,
      name: name.trim(),
      unit: dbUnit,
      status,
      quantity: toBaseValue(parseFloat(quantity) || 0, displayUnit),
    };
    if (catalogStrain) body.catalog_strain = catalogStrain;
    if (species.trim()) body.species = species.trim();
    if (generation.trim()) body.generation = generation.trim();
    if (containerType) body.container_type = containerType;
    if (startedAt) body.started_at = new Date(startedAt).toISOString();
    if (inoculatedAt) body.inoculated_at = new Date(inoculatedAt).toISOString();
    if (readyAt) body.ready_at = new Date(readyAt).toISOString();
    if (deviceId) body.device_id = Number(deviceId);
    if (lowStockThreshold) body.low_stock_threshold = toBaseValue(parseFloat(lowStockThreshold), displayUnit);
    if (sourceName.trim()) body.source_name = sourceName.trim();
    if (sourceUrl.trim()) body.source_url = sourceUrl.trim();
    if (referralLinkId) body.referral_link_id = Number(referralLinkId);
    if (defaultDeductQty) body.default_deduct_qty = parseFloat(defaultDeductQty);
    if (notes.trim()) body.notes = notes.trim();
    if (parentItemId) body.parent_item_id = Number(parentItemId);
    const resolvedCost = costMode === "calc"
      ? (parseFloat(costQty) > 0 ? parseFloat(costTotal) / parseFloat(costQty) : null)
      : (costPerUnit ? parseFloat(costPerUnit) : null);
    if (resolvedCost != null && !isNaN(resolvedCost)) body.cost_per_unit = resolvedCost;
    if (expiresAt) body.expires_at = new Date(expiresAt).toISOString();

    try {
      const item = await createItem.mutateAsync(body);
      navigate(`/inventory/${item.id}`);
    } catch (err) {
      setError(err.message ?? "Failed to create item");
    }
  }

  const hint = storageHint(displayUnit);

  const inputCls = "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500";
  const selectCls = `${inputCls} bg-white`;
  const labelCls = "block text-sm font-medium text-gray-700 mb-1";

  return (
    <LayoutWide title="New Inventory Item">
      {showUpgrade && <UpgradeModal current={itemCount} limit={limit} onClose={() => setShowUpgrade(false)} />}
      <div className="max-w-lg mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => navigate("/inventory")}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Back"
          >
            ←
          </button>
          <h1 className="text-2xl font-semibold text-cyan-800">New Inventory Item</h1>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className={labelCls}>Category <span className="text-red-500">*</span></label>
            <select value={category} onChange={(e) => handleCategoryChange(e.target.value)} className={selectCls}>
              {CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className={labelCls}>Name <span className="text-red-500">*</span></label>
            <input
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. PE6 Liquid Culture"
              className={inputCls}
            />
          </div>

          {isBio && (
            <div>
              <label className={labelCls}>Strain</label>
              <input
                type="text"
                list="catalog-list"
                value={catalogStrain}
                onChange={(e) => handleCatalogSelection(e.target.value)}
                placeholder="Start typing a strain name…"
                className={inputCls}
              />
              <datalist id="catalog-list">
                {catalog.map((e) => (
                  <option key={e.id} value={e.common_name} />
                ))}
              </datalist>
            </div>
          )}

          {isBio && (
            <div>
              <label className={labelCls}>Species</label>
              <input
                type="text"
                value={species}
                onChange={(e) => setSpecies(e.target.value)}
                placeholder="e.g. Psilocybe cubensis"
                className={inputCls}
              />
            </div>
          )}

          {isBio && (
            <div>
              <label className={labelCls}>Generation</label>
              <input
                type="text"
                value={generation}
                onChange={(e) => setGeneration(e.target.value)}
                placeholder="e.g. LC Gen 3, Agar Sub-culture 2"
                className={inputCls}
              />
            </div>
          )}

          {isBio && (
            <div>
              <label className={labelCls}>Container type</label>
              <select value={containerType} onChange={(e) => setContainerType(e.target.value)} className={selectCls}>
                <option value="">— none —</option>
                {CONTAINER_TYPES.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
          )}

          {showParent && (
            <div>
              <label className={labelCls}>Spawned from</label>
              <select value={parentItemId} onChange={(e) => setParentItemId(e.target.value)} className={selectCls}>
                <option value="">— none —</option>
                {parentOptions.map((i) => (
                  <option key={i.id} value={i.id}>{i.name} — {CATEGORY_LABELS[i.category] ?? i.category}</option>
                ))}
              </select>
            </div>
          )}

          <div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Quantity <span className="text-red-500">*</span></label>
                <input
                  type="number"
                  min="0"
                  step="any"
                  required
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  placeholder="0"
                  className={inputCls}
                />
              </div>
              <div>
                <label className={labelCls}>Unit <span className="text-red-500">*</span></label>
                <select value={displayUnit} onChange={(e) => setDisplayUnit(e.target.value)} className={selectCls}>
                  {UNIT_GROUPS.map((group) => (
                    <optgroup key={group.label} label={group.label}>
                      {group.units.map((u) => (
                        <option key={u.display} value={u.display}>{u.display}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
                {hint && (
                  <p className="text-xs text-gray-400 mt-1">{hint}</p>
                )}
              </div>
            </div>
          </div>

          <div>
            <label className={labelCls}>Status</label>
            <select value={status} onChange={(e) => setStatus(e.target.value)} className={selectCls}>
              {STATUSES.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>

          {isBio && (
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className={labelCls}>Started at</label>
                <input type="date" value={startedAt} onChange={(e) => setStartedAt(e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Inoculated at</label>
                <input type="date" value={inoculatedAt} onChange={(e) => setInoculatedAt(e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Ready at</label>
                <input type="date" value={readyAt} onChange={(e) => setReadyAt(e.target.value)} className={inputCls} />
              </div>
            </div>
          )}

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className={labelCls + " mb-0"}>Cost per {displayUnit}</label>
              <div className="flex gap-2 text-xs">
                <button type="button" onClick={() => setCostMode("direct")}
                  className={`px-2 py-0.5 rounded ${costMode === "direct" ? "bg-cyan-100 text-cyan-700 font-medium" : "text-gray-400 hover:text-gray-600"}`}>
                  Direct
                </button>
                <button type="button" onClick={() => setCostMode("calc")}
                  className={`px-2 py-0.5 rounded ${costMode === "calc" ? "bg-cyan-100 text-cyan-700 font-medium" : "text-gray-400 hover:text-gray-600"}`}>
                  Qty ÷ Total
                </button>
              </div>
            </div>
            {costMode === "direct" ? (
              <input
                type="number"
                min="0"
                step="any"
                value={costPerUnit}
                onChange={(e) => setCostPerUnit(e.target.value)}
                placeholder="Optional"
                className={inputCls}
              />
            ) : (
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="0"
                  step="any"
                  value={costQty}
                  onChange={(e) => setCostQty(e.target.value)}
                  placeholder={`Qty (${displayUnit})`}
                  className={inputCls}
                />
                <span className="text-gray-400 text-sm shrink-0">÷</span>
                <input
                  type="number"
                  min="0"
                  step="any"
                  value={costTotal}
                  onChange={(e) => setCostTotal(e.target.value)}
                  placeholder="Total cost $"
                  className={inputCls}
                />
                <span className="text-gray-400 text-sm shrink-0">=</span>
                <span className="text-sm font-medium text-cyan-700 whitespace-nowrap tabular-nums">
                  {costQty && costTotal && parseFloat(costQty) > 0
                    ? `$${(parseFloat(costTotal) / parseFloat(costQty)).toFixed(4)}`
                    : "—"}
                </span>
              </div>
            )}
          </div>

          {showExpiry && (
            <div>
              <label className={labelCls}>Expires</label>
              <input
                type="date"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
                className={inputCls}
              />
            </div>
          )}

          {isBom && (
            <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-500">
              BOM ingredients can be added after the item is created.
            </div>
          )}

          <div>
            <label className={labelCls}>Storage device</label>
            <DeviceSelect value={deviceId} onChange={(e) => setDeviceId(e.target.value)} />
          </div>

          <div>
            <label className={labelCls}>Low stock threshold</label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="0"
                step="any"
                value={lowStockThreshold}
                onChange={(e) => setLowStockThreshold(e.target.value)}
                placeholder="Alert when quantity falls to or below…"
                className={inputCls}
              />
              <span className="text-sm text-gray-500 whitespace-nowrap">{displayUnit}</span>
            </div>
          </div>

          <div>
            <label className={labelCls}>Default deduct quantity</label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="0"
                step="any"
                value={defaultDeductQty}
                onChange={(e) => setDefaultDeductQty(e.target.value)}
                placeholder="e.g. 1"
                className={inputCls}
              />
              <span className="text-sm text-gray-500 whitespace-nowrap">{displayUnit}</span>
            </div>
            <p className="text-xs text-gray-400 mt-1">Pre-fills the deduct field when starting a grow with this item</p>
          </div>

          <div>
            <button
              type="button"
              onClick={() => setShowSource((v) => !v)}
              className="text-sm text-cyan-600 hover:text-cyan-800 font-medium"
            >
              {showSource ? "▾" : "▸"} Source / Reorder info
            </button>
            {showSource && (
              <div className="mt-3 space-y-3 pl-3 border-l-2 border-gray-200">
                {referralLinks.length > 0 && (
                  <div>
                    <label className={labelCls}>Reorder link</label>
                    <select value={referralLinkId} onChange={(e) => setReferralLinkId(e.target.value)} className={selectCls}>
                      <option value="">— none —</option>
                      {referralLinks.map((l) => (
                        <option key={l.id} value={l.id}>{l.name}{l.supplier ? ` (${l.supplier})` : ""}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div>
                  <label className={labelCls}>Source name</label>
                  <input type="text" value={sourceName} onChange={(e) => setSourceName(e.target.value)} placeholder="e.g. Mush Genetics" className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Source URL</label>
                  <input type="url" value={sourceUrl} onChange={(e) => setSourceUrl(e.target.value)} placeholder="https://…" className={inputCls} />
                </div>
              </div>
            )}
          </div>

          <div>
            <label className={labelCls}>Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className={inputCls}
            />
          </div>

          <UnitConverter />

          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {error}
            </div>
          )}

          {atLimit && (
            <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-800">
              You&apos;ve reached your Hobby plan limit ({itemCount}/{limit} items).{" "}
              <button type="button" onClick={() => setShowUpgrade(true)} className="underline font-medium">Learn more</button>
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button
              type={atLimit ? "button" : "submit"}
              onClick={atLimit ? () => setShowUpgrade(true) : undefined}
              disabled={(!atLimit && (createItem.isPending || !name.trim())) || planLoading}
              className="flex-1 bg-cyan-600 text-white rounded-lg px-4 py-2 text-sm font-medium hover:bg-cyan-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {planLoading ? "Loading…" : atLimit ? "Plan limit reached" : createItem.isPending ? "Saving…" : "Create item"}
            </button>
            <button
              type="button"
              onClick={() => navigate("/inventory")}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </LayoutWide>
  );
}
